import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useEntityAction } from "@/lib/mutations";
import type { Booking, BookingLineDetail, DriverGuide } from "@/lib/types";
import { isOwnDriver } from "@/lib/bookingLines";
import { useDrivers } from "@/components/TaskManager";
import { Button, Card, ErrorNote, Modal } from "@/components/ui";
import { ConfirmButton } from "@/components/ConfirmButton";
import { Labeled, TextField } from "@/components/Form";
import { formatMinor, minorToInput, toMinor } from "@/lib/hooks";
import { formatCompanyDateTime } from "@/lib/time";

type Holder = "agent" | "driver";

/** Which «Оплачено агенту / водителю» buttons make sense for this booking:
 * the agent one only when the booking came from a travel agent; the driver one
 * only when a live line has a contractor driver or guide — our own driver's
 * money is ours at once (rule 21), so there is nothing to collect from them.
 * A driver missing from the list is treated as a contractor (keep the button). */
export function collectorHolders(
  booking: Pick<Booking, "agent">,
  lines: BookingLineDetail[],
  drivers: DriverGuide[],
): Holder[] {
  const byId = new Map(drivers.map((d) => [d.id, d]));
  const contractor = (id: string | null) => !!id && !isOwnDriver(byId.get(id) ?? undefined);
  const hasContractor = lines.some(
    (l) =>
      !l.deleted_at &&
      l.task_status !== "cancelled" &&
      (contractor(l.driver_guide_id) || (l.guide_included && contractor(l.guide_driver_id))),
  );
  const out: Holder[] = [];
  if (booking.agent) out.push("agent");
  if (hasContractor) out.push("driver");
  return out;
}

/** «Кто получил деньги» on the booking page (migration 0053,
 * `storage.booking_collection`): the tourist paid the travel agent or the
 * driver → the booking is not «unpaid», the analyst asks the owner to collect
 * it; «Деньги получены» records the payment once it reaches us. */
export function BookingMoneyCollector({
  booking,
  balanceMinor,
  canWrite,
}: {
  booking: Booking;
  /** Default amount for the dialog — what the tourist still owes. */
  balanceMinor: number | undefined;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const ref = booking.booking_number;
  const [dialog, setDialog] = useState<Holder | null>(null);
  const [amount, setAmount] = useState("");
  const [tried, setTried] = useState(false);
  const keys = [["booking", ref], ["bookings"], ["finance-lines", ref]];
  const save = useEntityAction(keys, {
    onSuccess: () => setDialog(null),
    inlineError: true,
  });
  const act = useEntityAction(keys);
  // Same keys as the lines section / task manager — shared cache, no extra fetch.
  const linesQuery = useQuery({
    queryKey: ["booking-lines", ref],
    queryFn: () => api.get<BookingLineDetail[]>(`/bookings/${ref}/lines`),
  });
  const drivers = useDrivers();

  const by = booking.money_collected_by;
  const held = (by === "agent" || by === "driver") && booking.money_collected_minor;
  const who = (h: Holder) => t(h === "agent" ? "moneyCollector.fromAgent" : "moneyCollector.fromDriver");

  if (!held && !canWrite) return null;
  if (["cancelled", "refunded"].includes(booking.status) && !held) return null;
  if (!held) {
    // Nothing to show until we know who could hold the money; nobody → no card.
    if (!linesQuery.data || !drivers.data) return null;
  }
  const holders = held
    ? []
    : collectorHolders(booking, linesQuery.data ?? [], drivers.data?.items ?? []);
  if (!held && holders.length === 0) return null;

  const open = (h: Holder) => {
    setAmount(minorToInput(balanceMinor && balanceMinor > 0 ? balanceMinor : null));
    setTried(false);
    setDialog(h);
  };
  const minor = toMinor(amount);
  const amountBad = !minor;
  const submit = () => {
    setTried(true);
    if (amountBad || !dialog) return;
    save.mutate(() =>
      api.post(`/bookings/${ref}/money-collected`, {
        collected_by: dialog,
        amount_minor: minor,
      }),
    );
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-xs text-sand-muted">
          {t("moneyCollector.title")}
        </span>
        {held ? (
          booking.money_handed_over_at ? (
            <>
              <span>
                {t("moneyCollector.handedOverFrom", {
                  who: who(by as Holder),
                  when: formatCompanyDateTime(booking.money_handed_over_at),
                })}
                {" — "}
                <strong>{formatMinor(booking.money_collected_minor!, booking.currency)}</strong>
              </span>
              {canWrite && (
                <ConfirmButton
                  className="ml-auto"
                  label={t("moneyCollector.undoHandover")}
                  busy={act.isPending}
                  onConfirm={() =>
                    act.mutate(() => api.post(`/bookings/${ref}/money-collected/handover/undo`))
                  }
                />
              )}
            </>
          ) : (
            <>
              <span className="text-gold">
                {t(by === "agent" ? "moneyCollector.withAgent" : "moneyCollector.withDriver")}:{" "}
                <strong>{formatMinor(booking.money_collected_minor!, booking.currency)}</strong>
              </span>
              {canWrite && (
                <span className="ml-auto flex items-center gap-2">
                  <ConfirmButton
                    label={t("moneyCollector.clear")}
                    busy={act.isPending}
                    onConfirm={() =>
                      act.mutate(() =>
                        api.post(`/bookings/${ref}/money-collected`, { collected_by: "owner" }),
                      )
                    }
                  />
                </span>
              )}
            </>
          )
        ) : (
          <>
            <span className="text-sand-muted">{t("moneyCollector.owner")}</span>
            <span className="ml-auto flex flex-wrap gap-2">
              {holders.includes("agent") && (
                <Button className="px-3 py-1 text-xs" onClick={() => open("agent")}>
                  {t("moneyCollector.paidToAgent")}
                </Button>
              )}
              {holders.includes("driver") && (
                <Button className="px-3 py-1 text-xs" onClick={() => open("driver")}>
                  {t("moneyCollector.paidToDriver")}
                </Button>
              )}
            </span>
          </>
        )}
      </div>
      {held && !booking.money_handed_over_at && (
        <p className="mt-2 text-xs text-sand-muted">{t("moneyCollector.hint")}</p>
      )}

      {dialog && (
        <Modal
          size="sm"
          busy={save.isPending}
          title={t(
            dialog === "agent" ? "moneyCollector.dialogTitleAgent" : "moneyCollector.dialogTitleDriver",
          )}
          onClose={() => setDialog(null)}
        >
          <Labeled
            required
            invalid={tried && amountBad}
            error={tried && amountBad ? t("moneyCollector.amountMissing") : undefined}
            label={t("moneyCollector.amount", {
              who: t(dialog === "agent" ? "moneyCollector.agentWho" : "moneyCollector.driverWho"),
              currency: booking.currency,
            })}
          >
            <TextField
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
            />
          </Labeled>
          <p className="mt-3 text-xs text-sand-muted">{t("moneyCollector.hint")}</p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded-md px-3 py-2 text-sm text-sand-muted hover:text-sand"
              onClick={() => setDialog(null)}
              disabled={save.isPending}
            >
              {t("common.cancel")}
            </button>
            <Button loading={save.isPending} onClick={submit}>
              {t("common.save")}
            </Button>
          </div>
          {save.isError && (
            <ErrorNote message={(save.error as Error).message} />
          )}
        </Modal>
      )}
    </Card>
  );
}
